/**
 * PIEPastReflectionsCard — surfaces the user's saved hands-free
 * voice-journal entries on the Mirror dashboard. Renders ONLY when
 * at least one saved reflection exists so first-time users don't see
 * an empty placeholder.
 *
 * Each row is replay-on-tap (uses the same SpeechSynthesis voice the
 * user has configured for the coach) so commuters can re-listen
 * without typing. A small "🎧 N this week" pill in the header tracks
 * how often they've been wrapping up sessions — encourages the habit
 * loop without being a notification nag.
 *
 * Backend: GET /api/personal/coach/reflection-summary/list?only_saved=true
 */
import React, { useEffect, useState } from 'react';
import { Headphones, BookMarked, Play, Square, ChevronDown, ChevronUp } from 'lucide-react';
import CoachAvatar from '../../../components/coach/CoachAvatar';
import useCoachVoice from '../../../hooks/useCoachVoice';
import authedFetch from '../../../lib/authedFetch';
import { pieApi } from '../pieApi';
import logger from '../../../lib/logger';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: count entries created within the last 7 days for the
// streak pill. Cheaper than a server roundtrip and naturally adapts
// to whatever timezone the client is in.
function countThisWeek(rows) {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return rows.filter((r) => {
    const ts = new Date(r.created_at).getTime();
    return Number.isFinite(ts) && ts >= cutoff;
  }).length;
}

function formatRowDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = diffMs / (60 * 60 * 1000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${Math.floor(diffH)}h ago`;
  if (diffH < 24 * 7) return `${Math.floor(diffH / 24)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function PIEPastReflectionsCard() {
  const [rows, setRows] = useState(null); // null = loading; [] = none; [...] = rendered
  const [personas, setPersonas] = useState({});
  const [coachPrefs, setCoachPrefs] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const { speak, cancel, speaking, supported: voiceSupported } = useCoachVoice();

  // Tail-side effect: when the engine reports it stopped speaking,
  // clear the local "currently playing" highlight so the row reverts
  // from a Stop button back to a Play button.
  useEffect(() => {
    if (!speaking && playingId) setPlayingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaking]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      pieApi.listReflectionSummaries(true, 8).catch((e) => {
        logger.warn('past reflections load failed', e);
        return { summaries: [] };
      }),
      authedFetch(`${API}/api/coach/personas`).then((r) => r.ok ? r.json() : { personas: [] }).catch(() => ({ personas: [] })),
      authedFetch(`${API}/api/coach/preferences`).then((r) => r.ok ? r.json() : null).catch(() => null),
    ]).then(([summaryRes, catalogRes, prefsRes]) => {
      if (!mounted) return;
      setRows(summaryRes.summaries || []);
      const byId = {};
      (catalogRes.personas || []).forEach((p) => { byId[p.id] = p; });
      setPersonas(byId);
      setCoachPrefs(prefsRes);
    });
    return () => { mounted = false; };
  }, []);

  // Hide entirely until we know how many entries exist — and skip
  // the card altogether for users who haven't saved any.
  if (rows === null) return null;
  if (rows.length === 0) return null;

  const thisWeek = countThisWeek(rows);
  const visible = expanded ? rows : rows.slice(0, 3);

  const onReplay = (row) => {
    if (!voiceSupported) return;
    if (playingId === row.id) {
      cancel();
      setPlayingId(null);
      return;
    }
    cancel();
    setPlayingId(row.id);
    speak(row.summary, {
      voiceUri: coachPrefs?.voice_uri,
      rate: coachPrefs?.voice_rate || 1.0,
      muted: false,
    });
  };

  return (
    <section
      className="pie-card pie-past-reflections"
      data-testid="pie-past-reflections"
      aria-label="Your saved voice reflections"
    >
      <header className="pie-past-reflections-head">
        <div className="pie-past-reflections-title">
          <BookMarked size={14} />
          <span>Past reflections</span>
        </div>
        {thisWeek > 0 && (
          <span className="pie-past-reflections-streak" data-testid="pie-past-reflections-streak">
            <Headphones size={11} />
            <span>{thisWeek} this week</span>
          </span>
        )}
      </header>
      <p className="pie-past-reflections-sub">
        Voice wrap-ups you saved from your hands-free coaching sessions.
        Tap any row to replay it.
      </p>

      <ul className="pie-past-reflections-list" data-testid="pie-past-reflections-list">
        {visible.map((row) => {
          const persona = row.persona_id ? personas[row.persona_id] : null;
          const isPlaying = playingId === row.id;
          return (
            <li
              key={row.id}
              className={`pie-past-reflections-row ${isPlaying ? 'pie-past-reflections-row-playing' : ''}`}
              data-testid={`pie-past-reflections-row-${row.id}`}
            >
              <div className="pie-past-reflections-row-avatar">
                <CoachAvatar persona={persona} size={40} />
              </div>
              <div className="pie-past-reflections-row-body">
                <div className="pie-past-reflections-row-meta">
                  <span className="pie-past-reflections-row-name">
                    {persona?.name || 'Coach'}
                  </span>
                  <span className="pie-past-reflections-row-dot">·</span>
                  <span className="pie-past-reflections-row-date">{formatRowDate(row.created_at)}</span>
                </div>
                <p className="pie-past-reflections-row-text">
                  {row.summary}
                </p>
              </div>
              {voiceSupported && (
                <button
                  type="button"
                  className="pie-past-reflections-row-play"
                  onClick={() => onReplay(row)}
                  data-testid={`pie-past-reflections-play-${row.id}`}
                  aria-label={isPlaying ? 'Stop replay' : 'Replay this reflection'}
                  title={isPlaying ? 'Stop' : 'Replay'}
                >
                  {isPlaying ? <Square size={14} /> : <Play size={14} />}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {rows.length > 3 && (
        <button
          type="button"
          className="pie-past-reflections-toggle"
          onClick={() => setExpanded((v) => !v)}
          data-testid="pie-past-reflections-toggle"
        >
          {expanded
            ? <><ChevronUp size={12} /> Show less</>
            : <><ChevronDown size={12} /> Show all {rows.length}</>}
        </button>
      )}
    </section>
  );
}
